'use client'

import ReverseGeocodeService from '@/contexts/MapServiceProvider/ReverseGeocodeService'
import useAuth from '@/hooks/useAuth'
import {
  selectAvailableTimeSlots,
  selectStoreOrder,
} from '@/redux/order/selector'
import {
  useSyncOrderMethodMutation,
  useUpdateCollectionDateTimeMutation,
  useUpdateDeliveryLocationMutation,
  useUpdatePickupLocationMutation,
} from '@/services/local'
import {
  convertAddressSearchInput,
  storeOrderToDeliveryAddress,
  toDeliveryAddress,
} from '@/utils/address'
import { isEmpty } from '@/utils/string'
import { toast } from 'oddle-shop-monorepo.design-system'
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { createRoot } from 'react-dom/client'
import { useSelector } from 'react-redux'
import { dateToRequestString } from '@/utils/routes'

type OrderSetupContextType = {
  orderMethod: string | null
  storeId: string | null
  deliveryAddress: Oms.UpdateDeliveryLocationPayload | null
  selectedDateTime: Oms.AvailableTimeSlots | null
  isOrderSetupComplete: boolean
  skipAddress: boolean
  setOrderMethod: (method: string) => Promise<void>
  setStore: (storeId: string) => Promise<void>
  setDeliveryAddress: (params: {
    selectedSavedAddress: string | null
    deliveryAddress: MapAddress.AddressSearchResult
    addressTwo?: string | null
  }) => Promise<void>
  setSelectedDateTime: (
    date: string | Date,
    slot: Oms.AvailableTimeSlots
  ) => Promise<void>
  resetOrderSetup: () => void
  forceFinishOrderSetup: () => void
  isLoading: boolean
  isForceFinished: boolean
}

const OrderSetupContext = createContext<OrderSetupContextType | undefined>(
  undefined
)

export const useOrderSetup = () => {
  const context = useContext(OrderSetupContext)
  if (context === undefined) {
    throw new Error('useOrderSetup must be used within an OrderSetupProvider')
  }
  return context
}

const initialState = {
  orderMethod: null,
  storeId: null,
  deliveryAddress: null,
  selectedDateTime: null,
  isOrderSetupComplete: false,
  skipAddress: false,
  isForceFinished: false,
} as {
  orderMethod: string | null
  storeId: string | null
  deliveryAddress: Oms.UpdateDeliveryLocationPayload | null
  selectedDateTime: Oms.AvailableTimeSlots | null
  isOrderSetupComplete: boolean
  skipAddress: boolean
  isForceFinished: boolean
}

const OrderSetupProvider = ({ children }: { children: ReactNode }) => {
  const [setupState, setSetupState] = useState(initialState)
  const storeOrder = useSelector(selectStoreOrder)
  const availableTimeSlots = useSelector(selectAvailableTimeSlots)
  const initialized = useRef(false)

  const { isPickup, storeAddress, deliveryAddress, defaultStoreId } =
    storeOrder.data || {}

  const isAppointmentReady =
    (isPickup && !!storeAddress && !defaultStoreId) ||
    (!isPickup && !!deliveryAddress)

  const { data: userData } = useAuth()
  const [syncOrderMethod] = useSyncOrderMethodMutation()
  const [updateDeliveryLocation] = useUpdateDeliveryLocationMutation()
  const [updatePickupLocation] = useUpdatePickupLocationMutation()
  const [updateCollectionDateTime] = useUpdateCollectionDateTimeMutation()
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (isAppointmentReady && !initialized.current) {
      initialized.current = true
      setSetupState((prev) => ({
        ...prev,
        isOrderSetupComplete: true,
      }))
    }
  }, [isAppointmentReady])

  const setOrderMethod = useCallback(
    async (method: string) => {
      let skipAddress = !!storeOrder?.data?.deliveryAddress // true = here's delivery address saved to store order on client-side

      if (
        (storeOrder?.data?.isPickup && method === 'delivery') ||
        (!storeOrder?.data?.isPickup && method === 'pickup')
      ) {
        setIsLoading(true)
        await syncOrderMethod({
          storeOrderId: storeOrder.data?.id,
          method,
        })
        if (method === 'delivery') {
          const hasPreferredDeliveryAddress =
            !!userData?.data?.payload?.preferredDeliveryAddress

          let address: Oms.UpdateDeliveryLocationPayload | undefined

          if (!skipAddress && hasPreferredDeliveryAddress) {
            // If delivery address is not set in store order & there's preferred delivery address
            address = toDeliveryAddress(
              userData?.data?.payload?.preferredDeliveryAddress
            )
          } else if (skipAddress) {
            // If there is delivery address set in store order
            address = storeOrderToDeliveryAddress(storeOrder.data)
          }

          skipAddress = false // Make sure that the address dialog is not skipped if anything went wrong

          // Always sync delivery address since backend won't save delivery request when user toggle between pick up & delivery
          if (address) {
            const syncDeliveryLocation = await updateDeliveryLocation({
              address,
              storeOrderId: storeOrder.data?.id,
            })

            if (syncDeliveryLocation.data?.data?.payload?.storeOrder?.id) {
              skipAddress = true
            }
          }
        }
        setSetupState((prev) => ({
          ...prev,
          orderMethod: method,
          storeId: null,
          deliveryAddress: null,
          skipAddress,
        }))
        setIsLoading(false)
      }
    },
    [
      storeOrder?.data,
      syncOrderMethod,
      updateDeliveryLocation,
      userData?.data?.payload?.preferredDeliveryAddress,
    ]
  )

  const setStore = useCallback(
    async (storeId: string) => {
      setIsLoading(true)
      await updatePickupLocation({
        storeId,
        storeOrderId: storeOrder?.data?.id,
      })
      setSetupState((prev) => ({ ...prev, storeId: storeId }))
      setIsLoading(false)
    },
    [storeOrder?.data?.id, updatePickupLocation]
  )

  const setDeliveryAddress = useCallback(
    async ({
      selectedSavedAddress,
      deliveryAddress,
      addressTwo,
    }: {
      selectedSavedAddress: string | null
      deliveryAddress: MapAddress.AddressSearchResult
      addressTwo?: string | null
    }) => {
      setIsLoading(true)
      const selectedAddress = selectedSavedAddress
        ? (userData?.data?.payload?.deliveryAddresses || []).find(
            ({ id }) => id === selectedSavedAddress
          )
        : null

      let address: Oms.UpdateDeliveryLocationPayload

      if (selectedAddress) {
        if (isEmpty(selectedAddress.autoCompleteAddress)) {
          // Use ReverseGeocodeService to get address details
          const reverseGeocodePromise =
            new Promise<Oms.UpdateDeliveryLocationPayload>((resolve) => {
              const ReverseGeocodeWrapper = () => (
                <ReverseGeocodeService>
                  {({ reverseGeocode, isLoading }) => {
                    if (!isLoading) {
                      reverseGeocode(
                        selectedAddress.latitude,
                        selectedAddress.longitude
                      ).then((data) => {
                        if (data && data.length > 0) {
                          resolve(
                            convertAddressSearchInput({
                              address:
                                data[0] as MapAddress.AddressSearchResult,
                            })
                          )
                        } else {
                          resolve(
                            convertAddressSearchInput({
                              address: {
                                ...selectedAddress,
                                addressOne: selectedAddress.formattedAddress,
                              },
                            })
                          )
                        }
                      })
                    }
                    return null
                  }}
                </ReverseGeocodeService>
              )
              // Render the wrapper component
              const root = document.createElement('div')
              document.body.appendChild(root)
              const rootElement = React.createElement(ReverseGeocodeWrapper)
              const reactRoot = createRoot(root)
              reactRoot.render(rootElement)
            })

          address = await reverseGeocodePromise
        } else {
          address = toDeliveryAddress(selectedAddress)
        }
      } else {
        address = convertAddressSearchInput({
          address: deliveryAddress,
          addressTwo,
        })
      }
      setSetupState((prev) => ({ ...prev, deliveryAddress: address }))
      setIsLoading(false)
    },
    [userData?.data?.payload?.deliveryAddresses]
  )

  const setSelectedDateTime = useCallback(
    async (date: string | Date, slot: Oms.AvailableTimeSlots) => {
      try {
        setIsLoading(true)
        const selectedTimeSlot = slot || availableTimeSlots.data?.[0]
        await updateCollectionDateTime({
          storeOrderId: storeOrder?.data?.id,
          collectionDate: dateToRequestString(date),
          collectionTimeSlot: selectedTimeSlot.y,
          collectionTimeSlotId: selectedTimeSlot.x,
        }).unwrap()
        setSetupState((prev) => ({
          ...prev,
          selectedDateTime: slot,
          isOrderSetupComplete: true,
        }))
        setIsLoading(false)
      } catch (error) {
        toast.error('Cannot set selected date time', {
          description: error instanceof Error ? error.message : 'Unknown error',
        })
        setIsLoading(false)
      }
    },
    [availableTimeSlots.data, storeOrder?.data?.id, updateCollectionDateTime]
  )

  const forceFinishOrderSetup = useCallback(() => {
    setSetupState((prev) => ({ ...prev, isForceFinished: true }))
  }, [])

  const resetOrderSetup = useCallback(() => {
    setSetupState(initialState)
  }, [])

  const value = {
    ...setupState,
    setOrderMethod,
    setStore,
    setDeliveryAddress,
    setSelectedDateTime,
    resetOrderSetup,
    forceFinishOrderSetup,
    isLoading,
  }

  return (
    <OrderSetupContext.Provider value={value}>
      {children}
    </OrderSetupContext.Provider>
  )
}

export default OrderSetupProvider
